import React, { FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  applyLegalHoldInAPI,
  applyRetentionPolicyInAPI,
  LegalHoldEvidence,
  releaseLegalHoldInAPI,
  RetentionPolicyEvidence,
} from '../services/eCMRService';

const retentionActionErrorMessage = 'No se pudo completar la acción de retención o bloqueo legal. Reintenta o contacta soporte.';

const RetentionLegalHoldWorkflows: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentId = id || '';
  const [retentionDays, setRetentionDays] = useState('3650');
  const [jurisdiction, setJurisdiction] = useState('');
  const [retentionReason, setRetentionReason] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [releaseReason, setReleaseReason] = useState('');
  const [retentionEvidence, setRetentionEvidence] = useState<RetentionPolicyEvidence | null>(null);
  const [legalHoldEvidence, setLegalHoldEvidence] = useState<LegalHoldEvidence | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const resetFeedback = () => {
    setMessage('');
    setError('');
  };

  const handleRetentionPolicy = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      const evidence = await applyRetentionPolicyInAPI(documentId, {
        retentionDays: Number(retentionDays),
        ...(jurisdiction ? { jurisdiction } : {}),
        ...(retentionReason ? { reason: retentionReason } : {}),
      });
      setRetentionEvidence(evidence);
      setMessage('Retention policy applied.');
    } catch (submitError) {
      setError(retentionActionErrorMessage);
    }
  };

  const handleApplyLegalHold = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      const evidence = await applyLegalHoldInAPI(documentId, holdReason);
      setLegalHoldEvidence(evidence);
      setMessage('Legal hold active.');
    } catch (submitError) {
      setError(retentionActionErrorMessage);
    }
  };

  const handleReleaseLegalHold = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      const evidence = await releaseLegalHoldInAPI(documentId, releaseReason);
      setLegalHoldEvidence(evidence);
      setMessage('Legal hold released.');
    } catch (submitError) {
      setError(retentionActionErrorMessage);
    }
  };

  if (!documentId) {
    return (
      <div className="container mx-auto p-4">
        <p className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">Missing API document id.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Retention and Legal Hold</h1>
          <p className="text-sm text-gray-600">
            Partial technical compliance baseline for API document {documentId}. Backend controls remain authoritative.
          </p>
        </div>
        <Link to="/" className="text-blue-600 hover:text-blue-900 underline">Back to documents</Link>
      </div>

      {message && <p className="mb-4 rounded border-l-4 border-green-500 bg-green-100 p-4 text-green-700">{message}</p>}
      {error && <p className="mb-4 rounded border-l-4 border-red-500 bg-red-100 p-4 text-red-700">{error}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        <form onSubmit={handleRetentionPolicy} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Retention Policy</h2>
          <p className="mb-3 text-sm text-gray-600">
            Applies a server-side retention policy evidence record. This is not a jurisdiction-specific legal opinion.
          </p>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="retentionDays">
            Retention days
            <input
              id="retentionDays"
              type="number"
              min="1"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={retentionDays}
              onChange={(event) => setRetentionDays(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="jurisdiction">
            Jurisdiction
            <input
              id="jurisdiction"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={jurisdiction}
              onChange={(event) => setJurisdiction(event.target.value)}
              placeholder="e.g. EU, ES, FR"
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="retentionReason">
            Retention reason
            <textarea
              id="retentionReason"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={retentionReason}
              onChange={(event) => setRetentionReason(event.target.value)}
            />
          </label>
          <button type="submit" className="rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Apply retention policy
          </button>

          {retentionEvidence && (
            <div className="mt-4 rounded bg-blue-50 p-3 text-sm text-blue-900">
              <p>Retention policy evidence: recorded</p>
              <p>
                Retention date: {
                  retentionEvidence.retainUntilPresent === true || Boolean(retentionEvidence.retainUntil)
                    ? 'available'
                    : 'not available'
                }
              </p>
              {retentionEvidence.retentionMode && <p>Mode: {retentionEvidence.retentionMode}</p>}
            </div>
          )}
        </form>

        <section className="bg-white shadow-md rounded p-4" aria-labelledby="legalHoldHeading">
          <h2 id="legalHoldHeading" className="mb-3 text-lg font-bold">Legal Hold</h2>
          <p className="mb-3 text-sm text-gray-600">
            Applies or releases a server-side legal hold baseline for dispute, authority, or preservation workflows.
          </p>
          <form onSubmit={handleApplyLegalHold} className="mb-4">
            <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="legalHoldReason">
              Legal hold reason
              <textarea
                id="legalHoldReason"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={holdReason}
                onChange={(event) => setHoldReason(event.target.value)}
                required
              />
            </label>
            <button type="submit" className="rounded bg-purple-600 px-4 py-2 font-bold text-white hover:bg-purple-700">
              Apply legal hold
            </button>
          </form>

          <form onSubmit={handleReleaseLegalHold}>
            <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="releaseReason">
              Release reason
              <textarea
                id="releaseReason"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={releaseReason}
                onChange={(event) => setReleaseReason(event.target.value)}
                required
              />
            </label>
            <button type="submit" className="rounded bg-amber-600 px-4 py-2 font-bold text-white hover:bg-amber-700">
              Release legal hold
            </button>
          </form>

          {legalHoldEvidence && (
            <div className="mt-4 rounded bg-purple-50 p-3 text-sm text-purple-900">
              <p>Legal hold evidence: recorded</p>
              <p>Status: {legalHoldEvidence.active ? 'active' : 'released'}</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default RetentionLegalHoldWorkflows;
